import styled from 'styled-components';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { ReadOnlyBanner, useAgentPermission } from '@entities/user';
import { riseIn } from '@shared/theme/animations';

/* Оболочка агента ОСНОВА.Совещания: левый рельс («Обзор») + основная область с
   заголовком и `<Outlet/>`. Минимальный каркас витрины (по образцу MtoLayout) —
   пункты рельса добавляются по мере вывода модулей на фронт. */

const Shell = styled.div`
  max-width: 1320px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 232px minmax(0, 1fr);
  gap: ${({ theme }) => theme.spacing(7)};
  align-items: start;

  @media (max-width: 920px) {
    grid-template-columns: minmax(0, 1fr);
    gap: ${({ theme }) => theme.spacing(5)};
  }
`;

const Rail = styled.nav`
  position: sticky;
  top: ${({ theme }) => theme.spacing(4)};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(0.5)};

  @media (max-width: 920px) {
    position: static;
    flex-direction: row;
    overflow-x: auto;
    gap: ${({ theme }) => theme.spacing(2)};
    padding-bottom: ${({ theme }) => theme.spacing(2)};
  }
`;

const Brand = styled(Link)`
  font-size: 17px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.colors.text.primary};
  text-decoration: none;
  margin-bottom: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2)}`};

  & .accent {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 800;
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
    border-radius: ${({ theme }) => theme.radius.sm};
  }

  @media (max-width: 920px) {
    display: none;
  }
`;

const RailLink = styled(Link)<{ $active: boolean }>`
  display: flex;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing(2)};
  font-size: 13.5px;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  border-left: 2px solid ${({ theme, $active }) => ($active ? theme.colors.primary : 'transparent')};
  background: ${({ theme, $active }) => ($active ? theme.colors.primarySoft : 'transparent')};
  color: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.text.secondary)};
  transition:
    background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }

  @media (max-width: 920px) {
    border-left: none;
    border: 1px solid ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.border)};
    border-radius: ${({ theme }) => theme.radius.pill};
  }
`;

const Main = styled.div`
  min-width: 0;
`;

const Header = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(6)};
  animation: ${riseIn} 420ms ${({ theme }) => theme.motion.easeOut} both;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.02em;

  & .accent {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 800;
  }
`;

const Subtitle = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 15px;
  margin-top: ${({ theme }) => theme.spacing(2)};
  max-width: 760px;
`;

/**
 * Оболочка агента ОСНОВА.Совещания (`/agents/meetings`). Постоянный левый рельс
 * («Обзор») + контекстный заголовок и `<Outlet/>` для дочерних страниц.
 */
export function MeetingsLayout() {
  const location = useLocation();
  const { can_read, can_write } = useAgentPermission('meetings');
  const isOverview = location.pathname.split('/').filter(Boolean).length === 2;

  return (
    <Shell>
      <Rail aria-label="Навигация агента Совещания">
        <Brand to="/agents/meetings">
          ОСНОВА<span className="accent">.Совещания</span>
        </Brand>
        <RailLink
          to="/agents/meetings"
          $active={isOverview}
          aria-current={isOverview ? 'page' : undefined}
        >
          Обзор
        </RailLink>
        <RailLink
          to="/agents/meetings/register"
          $active={location.pathname.endsWith('/register')}
          aria-current={location.pathname.endsWith('/register') ? 'page' : undefined}
        >
          Реестр
        </RailLink>
        <RailLink
          to="/agents/meetings/archive"
          $active={location.pathname.endsWith('/archive')}
          aria-current={location.pathname.endsWith('/archive') ? 'page' : undefined}
        >
          Поиск по архиву
        </RailLink>
        {can_write && (
          <RailLink
            to="/agents/meetings/departments"
            $active={location.pathname.endsWith('/departments')}
            aria-current={location.pathname.endsWith('/departments') ? 'page' : undefined}
          >
            Справочник отделов
          </RailLink>
        )}
        {can_write && (
          <RailLink
            to="/agents/meetings/employees"
            $active={location.pathname.endsWith('/employees')}
            aria-current={location.pathname.endsWith('/employees') ? 'page' : undefined}
          >
            Справочник сотрудников
          </RailLink>
        )}
      </Rail>

      <Main>
        <Header>
          <Title>
            ОСНОВА<span className="accent">.Совещания</span>
          </Title>
          <Subtitle>
            Автопротоколирование совещаний: запись → распознавание речи и диаризация → черновик
            протокола с таймкодами → утверждение организатором → реестр поручений с напоминаниями
            и эскалацией → живая сводка по серии.
          </Subtitle>
        </Header>
        {can_read && !can_write && <ReadOnlyBanner />}
        <Outlet />
      </Main>
    </Shell>
  );
}
